import { Calculator, BookOpen, Atom, TrendingUp, FlaskConical } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export interface Subtopic {
  id: string;
  name: string;
  questionCount?: number;
}

export interface Topic {
  id: string;
  name: string;
  questionCount: number;
  subtopics?: Subtopic[];
}

export interface Module {
  id: string;
  title: string;
  icon: React.ElementType; // Better type for Lucide icons
  description?: string;
  questionCount?: number;
  topics: Topic[];
}

export interface Subject {
  id: string;
  title: string; // e.g., "SAT", "AP Physics 1"
  description: string;
  image?: string; // Optional cover image
  color: string; // Tailwind bg color class prefix
  gradient: string; // Tailwind gradient class
  modules: Module[];
  comingSoon?: boolean;
}

export const SUBJECTS_DATA: Subject[] = [
  {
    id: "sat",
    title: "SAT®",
    description: "Prepare for the digital SAT with comprehensive Math and Reading & Writing practice.",
    color: "bg-blue-500",
    gradient: "from-blue-500 to-cyan-500",
    modules: [
      {
        id: "math",
        title: "Math",
        icon: Calculator,
        questionCount: 1245,
        topics: [
          {
            id: "algebra", name: "Algebra", questionCount: 312,
            subtopics: [
              { id: "linear_equations_in_one_variable", name: "Linear Equations in One Variable" },
              { id: "linear_equations_in_two_variables", name: "Linear Equations in Two Variables" },
              { id: "linear_functions", name: "Linear Functions" },
              { id: "systems_of_two_linear_equations_in_two_variables", name: "Systems of Two Linear Equations in Two Variables" },
              { id: "linear_inequalities_in_one_or_two_variables", name: "Linear Inequalities in One or Two Variables" },
            ],
          },
          {
            id: "advanced_math", name: "Advanced Math", questionCount: 278,
            subtopics: [
              { id: "nonlinear_functions", name: "Nonlinear Functions" },
              { id: "nonlinear_equations_in_one_variable", name: "Nonlinear Equations in One Variable" },
              { id: "systems_of_equations_in_two_variables", name: "Systems of Equations in Two Variables" },
              { id: "equivalent_expressions", name: "Equivalent Expressions" },
            ],
          },
          {
            id: "problem_solving_and_data_analysis", name: "Problem-Solving and Data Analysis", questionCount: 245,
            subtopics: [
              { id: "ratios_rates_proportional_relationships_and_units", name: "Ratios, Rates, Proportional Relationships, and Units" },
              { id: "percentages", name: "Percentages" },
              { id: "one_variable_data", name: "One-Variable Data" },
              { id: "two_variable_data", name: "Two-Variable Data" },
              { id: "probability_and_conditional_probability", name: "Probability and Conditional Probability" },
              { id: "inference_from_sample_statistics_and_margin_of_error", name: "Inference from Sample Statistics and Margin of Error" },
              { id: "evaluating_statistical_claims", name: "Evaluating Statistical Claims" },
            ],
          },
          {
            id: "geometry_and_trigonometry", name: "Geometry and Trigonometry", questionCount: 198,
            subtopics: [
              { id: "area_and_volume", name: "Area and Volume" },
              { id: "lines_angles_and_triangles", name: "Lines, Angles, and Triangles" },
              { id: "right_triangles_and_trigonometry", name: "Right Triangles and Trigonometry" },
              { id: "circles", name: "Circles" },
            ],
          },
        ],
      },
      {
        id: "reading_and_writing",
        title: "Reading & Writing",
        icon: BookOpen,
        questionCount: 1590,
        topics: [
          {
            id: "information_and_ideas", name: "Information and Ideas", questionCount: 415,
            subtopics: [
              { id: "central_ideas_and_details", name: "Central Ideas and Details" },
              { id: "command_of_evidence", name: "Command of Evidence" },
              { id: "inferences", name: "Inferences" },
            ],
          },
          {
            id: "craft_and_structure", name: "Craft and Structure", questionCount: 412,
            subtopics: [
              { id: "words_in_context", name: "Words in Context" },
              { id: "text_structure_and_purpose", name: "Text Structure and Purpose" },
              { id: "cross_text_connections", name: "Cross-Text Connections" },
            ],
          },
          {
            id: "expression_of_ideas", name: "Expression of Ideas", questionCount: 336,
            subtopics: [
              { id: "rhetorical_synthesis", name: "Rhetorical Synthesis" },
              { id: "transitions", name: "Transitions" },
            ],
          },
          {
            id: "standard_english_conventions", name: "Standard English Conventions", questionCount: 427,
            subtopics: [
              { id: "boundaries", name: "Boundaries" },
              { id: "form_structure_and_sense", name: "Form, Structure, and Sense" },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "act",
    title: "ACT®",
    description: "Master the ACT with detailed questions for English, Math, Reading, and Science.",
    color: "bg-red-500",
    gradient: "from-red-500 to-orange-500",
    comingSoon: true,
    modules: [
        {
            id: "english",
            title: "English",
            icon: BookOpen,
            topics: [
                {
                  id: "production_of_writing", name: "Production of Writing", questionCount: 200,
                  subtopics: [
                    { id: "topic_development", name: "Topic Development" },
                    { id: "organization_unity_cohesion", name: "Organization, Unity, and Cohesion" },
                  ],
                },
                {
                  id: "knowledge_of_language", name: "Knowledge of Language", questionCount: 150,
                  subtopics: [
                    { id: "knowledge_of_language", name: "Knowledge of Language" },
                  ],
                },
                {
                  id: "conventions_standard_english", name: "Conventions of Standard English", questionCount: 250,
                  subtopics: [
                    { id: "sentence_structure", name: "Sentence Structure and Formation" },
                    { id: "punctuation", name: "Punctuation" },
                    { id: "usage", name: "Usage" },
                  ],
                },
            ]
        },
        {
            id: "math",
            title: "Math",
            icon: Calculator,
            topics: [
                {
                  id: "number_quantity", name: "Number & Quantity", questionCount: 180,
                  subtopics: [
                    { id: "real_complex_numbers", name: "Real and Complex Numbers" },
                    { id: "rational_exponents", name: "Rational Exponents and Radicals" },
                    { id: "vectors_matrices", name: "Vectors and Matrices" },
                  ],
                },
                {
                  id: "algebra", name: "Algebra", questionCount: 220,
                  subtopics: [
                    { id: "linear_equations", name: "Linear Equations" },
                    { id: "polynomial_operations", name: "Polynomial Operations" },
                    { id: "polynomial_zeros", name: "Polynomial Zeros" },
                    { id: "solving_for_variable", name: "Solving for a Variable" },
                  ],
                },
                {
                  id: "functions", name: "Functions", questionCount: 200,
                  subtopics: [
                    { id: "sequences", name: "Sequences" },
                    { id: "exponential_logarithmic", name: "Exponential and Logarithmic Functions" },
                    { id: "radian_degree", name: "Radian and Degree Conversion" },
                    { id: "function_transformations", name: "Function Transformations" },
                    { id: "function_composition", name: "Function Composition" },
                  ],
                },
                {
                  id: "geometry", name: "Geometry", questionCount: 190,
                  subtopics: [
                    { id: "triangle_properties", name: "Triangle Properties" },
                    { id: "similar_triangles", name: "Similar Triangles and Proportions" },
                    { id: "coordinate_geometry", name: "Coordinate Geometry" },
                    { id: "circles_polygons", name: "Circles and Polygons" },
                    { id: "trigonometric_ratios", name: "Trigonometric Ratios" },
                  ],
                },
                {
                  id: "stats_prob", name: "Statistics & Probability", questionCount: 160,
                  subtopics: [
                    { id: "data_analysis", name: "Data Analysis and Statistics" },
                    { id: "probability", name: "Probability" },
                    { id: "conditional_probability", name: "Conditional Probability" },
                    { id: "expected_value", name: "Expected Value" },
                  ],
                },
                {
                  id: "integrating_essential_skills", name: "Integrating Essential Skills", questionCount: 0,
                  subtopics: [
                    { id: "average_median", name: "Average and Median" },
                    { id: "rates_unit_conversion", name: "Rates and Unit Conversion" },
                    { id: "ratios_proportions", name: "Ratios and Proportions" },
                    { id: "area_volume", name: "Area, Surface Area, and Volume" },
                    { id: "multi_step_applied", name: "Multi-Step Applied Problems" },
                  ],
                },
            ]
        },
        {
            id: "reading",
            title: "Reading",
            icon: BookOpen,
            topics: [
                {
                  id: "key_ideas_details", name: "Key Ideas & Details", questionCount: 210,
                  subtopics: [
                    { id: "key_ideas_details", name: "Key Ideas & Details" },
                  ],
                },
                {
                  id: "craft_structure", name: "Craft & Structure", questionCount: 190,
                  subtopics: [
                    { id: "craft_structure", name: "Craft & Structure" },
                  ],
                },
                {
                  id: "integration_knowledge_ideas", name: "Integration of Knowledge & Ideas", questionCount: 140,
                  subtopics: [
                    { id: "integration_knowledge_ideas", name: "Integration of Knowledge & Ideas" },
                  ],
                },
            ]
        },
        {
            id: "science",
            title: "Science",
            icon: FlaskConical,
            topics: [
                {
                  id: "interpretation_of_data", name: "Interpretation of Data", questionCount: 0,
                  subtopics: [
                    { id: "interpretation_of_data", name: "Interpretation of Data" },
                  ],
                },
                {
                  id: "scientific_investigation", name: "Scientific Investigation", questionCount: 0,
                  subtopics: [
                    { id: "scientific_investigation", name: "Scientific Investigation" },
                  ],
                },
                {
                  id: "evaluation_models_inferences", name: "Evaluation of Models, Inferences & Experimental Results", questionCount: 0,
                  subtopics: [
                    { id: "evaluation_models_inferences", name: "Evaluation of Models, Inferences & Experimental Results" },
                  ],
                },
            ]
        }
    ],
  },
  {
    id: "ap-physics-1",
    title: "AP® Physics 1",
    description: "Algebra-based physics covering mechanics, waves, and comprehensive rotational motion.",
    color: "bg-violet-500",
    gradient: "from-violet-500 to-purple-500",
    comingSoon: true,
    modules: [
      {
        id: "ap_physics_1",
        title: "Mechanics",
        icon: Atom,
        topics: [
          { id: "kinematics", name: "Kinematics", questionCount: 120 ,
          subtopics: [
            { id: "newton_s_laws_of_motion", name: "Newton’s laws of motion", questionCount: 0 },
          ],
        },
          { id: "dynamics", name: "Dynamics", questionCount: 150 },
          { id: "energy", name: "Energy", questionCount: 130 },
          { id: "momentum", name: "Momentum", questionCount: 110 },
          { id: "rotation", name: "Rotational Motion", questionCount: 140 },
        
          { id: "force_and_translational_dynamics", name: "Force and Translational Dynamics", questionCount: 0 ,
          subtopics: [
            { id: "free_body_diagrams", name: "Free Body Diagrams", questionCount: 0 },
          ],
        },
        
          { id: "fluids", name: "Fluids", questionCount: 0 },
        
          { id: "energy_and_momentum_of_rotating_systems", name: "Energy and Momentum of Rotating Systems", questionCount: 0 },
        ],
      },
    
      {
        id: "linear_momentum",
        title: "Linear Momentum",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "torque_and_rotational_dynamics",
        title: "Torque and Rotational Dynamics",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "oscillations",
        title: "Oscillations",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "work_energy_and_power",
        title: "Work, Energy, and Power",
        icon: BookOpen,
        topics: [],
      },
    ],
  },
    {
    id: "ap-calc-bc",
    title: "AP® Calculus BC",
    description: "Comprehensive calculus covering limits, derivatives, integrals, and series.",
    color: "bg-emerald-500",
    gradient: "from-emerald-500 to-teal-500",
    comingSoon: true,
    modules: [
      {
        id: "calculus",
        title: "Calculus",
        icon: TrendingUp,
        topics: [
          { id: "limits", name: "Limits & Continuity", questionCount: 80 },
          { id: "derivatives", name: "Differentiation", questionCount: 200 },
          { id: "integrals", name: "Integration", questionCount: 180 },
          { id: "series", name: "Infinite Series", questionCount: 150 },
        ],
      },
    ],
  },
  {
    id: "ap_physics_2",
    title: "AP Physics 2",
    description: "",
    color: "bg-gray-500",
    gradient: "from-gray-500 to-slate-500",
    comingSoon: true,
    modules: [
      {
        id: "electric_circuits",
        title: "Electric Circuits",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "electric_force_fields_and_potential",
        title: "Electric Force, Fields, and Potential",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "magnetism_and_electromagnetism",
        title: "Magnetism and Electromagnetism",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "geometric_optics",
        title: "Geometric Optics",
        icon: BookOpen,
        topics: [],
      },
    
      {
        id: "waves_sound_and_physical_optics",
        title: "Waves, Sound, and Physical Optics",
        icon: BookOpen,
        topics: [],
      },
    ],
  },
];
